import { Search } from '@tanstack/react-location';
import { createProvider } from '@frontend/store';
import { SettingsSearchParams } from './settings-search-param-types';
import { RouteParams } from './types';

type PathShape<T extends unknown = any> = {
  path: string;
  raw: string;
  search?: Search<unknown>;
  context?: T;
  replace?: boolean;
};

type MainRouterPathShape = Pick<PathShape, 'path' | 'replace' | 'search'>;

type PathHistory = PathShape & { label: string };

export type NavigationContext = {
  fullPath: string;
  params: RouteParams<any>;
  pathname: string;
  search: SettingsSearchParams<any>;
  isHandledBySettingsRouter: boolean;
};

export type ShouldBlockFn = (props: { current: NavigationContext; next: NavigationContext }) => boolean;

export type InterRouterStore<T extends unknown = any> = {
  isSettingsOpen: boolean;
  settingsRouterPath: PathShape;
  mainRouterPath: MainRouterPathShape;
  isSettingsMenuOpen: boolean;
  settingsRouterPathHistory: PathHistory[];
  originPageLabel: string;
  setOriginPageLabel: (label: string | undefined) => void;
  setIsSettingsOpen: (isOpen: boolean) => void;
  setSettingsRouterPathHistory: (path: PathHistory) => void;
  setSettingsRouterPath: (settingsRouterPath: PathShape<T>) => void;
  setMainRouterPath: (mainRouterPath: MainRouterPathShape) => void;
  getBackSettings: () => { lastItem: PathHistory; newHistory: PathHistory[] } | undefined;
  goBackSettings: () => PathShape | undefined;
  closeSettings: () => void;
  setIsSettingsMenuOpen: (isOpen: boolean) => void;
  context?: T;
  shouldBlockFn?: ShouldBlockFn;
  setShouldBlockFn: (shouldBlockFn: ShouldBlockFn | undefined) => void;
};

export const {
  Provider: InterRouterProvider,
  useStore: useInterRouter,
  useNonReactiveStore: useNonReactiveInterRouter,
} = createProvider<InterRouterStore>()((set, get) => ({
  context: undefined,
  isSettingsOpen: false,
  isSettingsMenuOpen: false,
  settingsRouterPath: { path: '', raw: '' },
  mainRouterPath: { path: '' },
  settingsRouterPathHistory: [],
  originPageLabel: '',
  setOriginPageLabel: (label) => {
    set(() => ({ originPageLabel: label }));
  },
  setSettingsRouterPath: ({ context, ...settingsRouterPath }) => {
    set((state) => {
      state.context = context;
      return { settingsRouterPath };
    });
  },
  setIsSettingsMenuOpen: (isOpen) => {
    set(() => ({ isSettingsMenuOpen: isOpen }));
  },
  setMainRouterPath: (mainRouterPath) => {
    set(() => ({ mainRouterPath }));
  },
  setSettingsRouterPathHistory: (path) => {
    set((state) => {
      const newHistory = path.replace
        ? [...state.settingsRouterPathHistory.slice(0, -1), path]
        : [...state.settingsRouterPathHistory, path];
      return { settingsRouterPathHistory: newHistory };
    });
  },
  setIsSettingsOpen: (isOpen) => {
    set(() => ({ isSettingsOpen: isOpen }));
  },
  goBackSettings: () => {
    const { lastItem, newHistory = [] } = get().getBackSettings() ?? {};
    set(() => ({ settingsRouterPathHistory: newHistory }));

    return lastItem;
  },
  getBackSettings: () => {
    const newHistory = [...get().settingsRouterPathHistory];
    newHistory.pop();
    const newLastMap = newHistory[newHistory.length - 1];

    return { lastItem: newLastMap, newHistory };
  },
  /**
   * Closes the settings panel and resets the router state.
   * This is called anytime the settings panel is closed (e.g. when clicking on the settings X, clicking outside
   * the settings modal, etc.).
   * @returns void
   */
  closeSettings: () => {
    const shouldBlockFn = get().shouldBlockFn;
    if (shouldBlockFn) {
      const next = {
        fullPath: '',
        params: {},
        pathname: '',
        search: {},
        isHandledBySettingsRouter: true,
      };
      const current = {
        fullPath: '',
        params: {},
        pathname: location.hash,
        search: location.search,
        isHandledBySettingsRouter: true,
      };
      // check if the shouldBlockFn is set and if it returns true, short circuit
      if (shouldBlockFn({ current, next })) return;
    }

    set({ settingsRouterPath: { path: '', raw: '' }, settingsRouterPathHistory: [], isSettingsOpen: false });
    history.pushState('', document.title, location.pathname + location.search);
  },
  setShouldBlockFn: (shouldBlockFn) => {
    set(() => ({ shouldBlockFn }));
  },
}));
